/**
 * Tab Settings Persistence
 *
 * Handles user preferences for tab behavior.
 */

import { DEFAULT_TAB_CONFIG, STORAGE_KEYS } from '@/config/tabsConfig';
import type { TabsConfig } from './types';

export type UserTabSettings = {
  /** Maximum number of open tabs */
  maxTabs: number;
  /** Restore tabs on login */
  persistRecentTabs: boolean;
  /** Maximum tabs to restore */
  maxRestoredTabs: number;
  /** Enable content caching (keep-alive) */
  enableContentCaching: boolean;
  /** Maximum cached tabs */
  maxCachedTabs: number;
  /** Confirm before closing multiple tabs */
  confirmCloseMultiple: boolean;
  /** Enable keyboard shortcuts */
  enableKeyboardShortcuts: boolean;
  /** Enable drag and drop reordering */
  enableDragReorder: boolean;
  /** Double-click tab action */
  doubleClickAction: 'pin' | 'none';
  /** Default behavior for sidebar clicks */
  defaultBehavior: 'new-tab' | 'replace' | 'reuse';
};

const DEFAULT_USER_SETTINGS: UserTabSettings = {
  maxTabs: DEFAULT_TAB_CONFIG.maxTabs,
  persistRecentTabs: DEFAULT_TAB_CONFIG.persistRecentTabs,
  maxRestoredTabs: DEFAULT_TAB_CONFIG.maxRestoredTabs,
  enableContentCaching: true,
  maxCachedTabs: 5,
  confirmCloseMultiple: DEFAULT_TAB_CONFIG.confirmCloseMultiple,
  enableKeyboardShortcuts: DEFAULT_TAB_CONFIG.enableKeyboardShortcuts,
  enableDragReorder: DEFAULT_TAB_CONFIG.enableDragReorder,
  doubleClickAction: 'pin',
  defaultBehavior: DEFAULT_TAB_CONFIG.defaultBehavior,
};

/**
 * Load user settings from localStorage
 */
export function loadUserSettings(): UserTabSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.TABS_SETTINGS);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new settings
      return { ...DEFAULT_USER_SETTINGS, ...parsed };
    }
  } catch (error) {
    console.error('Failed to load tab settings:', error);
  }
  return DEFAULT_USER_SETTINGS;
}

/**
 * Save user settings to localStorage
 */
export function saveUserSettings(settings: UserTabSettings): void {
  try {
    localStorage.setItem(STORAGE_KEYS.TABS_SETTINGS, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save tab settings:', error);
  }
}

/**
 * Reset settings to defaults
 */
export function resetUserSettings(): UserTabSettings {
  localStorage.removeItem(STORAGE_KEYS.TABS_SETTINGS);
  return DEFAULT_USER_SETTINGS;
}

/**
 * Merge user settings with base config
 */
export function mergeSettingsWithConfig(
  baseConfig: TabsConfig,
  userSettings: UserTabSettings
): TabsConfig {
  return {
    ...baseConfig,
    maxTabs: userSettings.maxTabs,
    persistRecentTabs: userSettings.persistRecentTabs,
    maxRestoredTabs: userSettings.maxRestoredTabs,
    confirmCloseMultiple: userSettings.confirmCloseMultiple,
    enableKeyboardShortcuts: userSettings.enableKeyboardShortcuts,
    enableDragReorder: userSettings.enableDragReorder,
    defaultBehavior: userSettings.defaultBehavior,
    enableContentCaching: userSettings.enableContentCaching,
    maxCachedTabs: userSettings.maxCachedTabs,
  };
}

export { DEFAULT_USER_SETTINGS };
